/**
 * priorityQueue.js — куча-минимум, O(log n) на вставку и извлечение.
 *
 * [10.0.3] Взят из присланного файла как есть: реализация верная, включая
 * неочевидное место в remove() — `pop()` перед проверкой `index < length`
 * корректно обрабатывает удаление последнего элемента.
 *
 * Смысл выделения в отдельный файл — убрать дублирование: ровно такой же
 * класс лежал внутри managers/NetworkQueue.js приватной копией. Теперь очередь
 * загрузки берёт его отсюда, и правка алгоритма не требует помнить о втором
 * экземпляре.
 *
 * Сравнение идёт по полю `priority`: меньше число — раньше очередь.
 */
export class PriorityQueue {
  #heap = [];

  get size() { return this.#heap.length; }
  isEmpty() { return this.#heap.length === 0; }

  enqueue(item) {
    this.#heap.push(item);
    this.#bubbleUp(this.#heap.length - 1);
  }

  dequeue() {
    if (this.isEmpty()) return null;
    const min = this.#heap[0];
    const last = this.#heap.pop();
    if (!this.isEmpty()) {
      this.#heap[0] = last;
      this.#sinkDown(0);
    }
    return min;
  }

  remove(predicate) {
    const index = this.#heap.findIndex(predicate);
    if (index === -1) return null;

    const item = this.#heap[index];
    const last = this.#heap.pop();

    // Если удаляли последний элемент, pop() его уже забрал — двигать нечего.
    if (index < this.#heap.length) {
      this.#heap[index] = last;
      this.#bubbleUp(index);
      this.#sinkDown(index);
    }

    return item;
  }

  #bubbleUp(i) {
    while (i > 0) {
      const parent = Math.floor((i - 1) / 2);
      if (this.#heap[i].priority >= this.#heap[parent].priority) break;
      [this.#heap[i], this.#heap[parent]] = [this.#heap[parent], this.#heap[i]];
      i = parent;
    }
  }

  #sinkDown(i) {
    const length = this.#heap.length;
    while (true) {
      const left = 2 * i + 1;
      const right = 2 * i + 2;
      let smallest = i;

      if (left < length && this.#heap[left].priority < this.#heap[smallest].priority) {
        smallest = left;
      }
      if (right < length && this.#heap[right].priority < this.#heap[smallest].priority) {
        smallest = right;
      }

      if (smallest === i) break;
      [this.#heap[i], this.#heap[smallest]] = [this.#heap[smallest], this.#heap[i]];
      i = smallest;
    }
  }
}

export default PriorityQueue;
